import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import {
  getDateRanges,
  getShowingText,
  translateGenericStatus,
} from "../../i18n/dashboardHelpers";
import { getCustomerStats, getCustomers } from "../../services/apiService";

const PAGE_SIZE = 20;

const tabs = [
  { id: "all", label: "All" },
  { id: "active", label: "Active", status: "active" },
  { id: "invited", label: "Invited", status: "invited" },
  { id: "suspended", label: "Suspended", status: "suspended" },
];

const statusStyles = {
  ACTIVE: "dashboard-badge--active",
  INVITED: "dashboard-badge--pending",
  SUSPENDED: "dashboard-badge--inactive",
};

function getCustomerName(customer) {
  return (
    customer.partnership?.full_name ||
    customer.partnership?.fullName ||
    customer.email ||
    customer.customer_id ||
    customer.customerId ||
    customer.id ||
    "—"
  );
}

function getPartnershipField(customer, field) {
  const snake = customer.partnership?.[field];
  if (snake != null && snake !== "") return snake;

  const camel = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  const camelValue = customer.partnership?.[camel];
  if (camelValue != null && camelValue !== "") return camelValue;

  return "—";
}

function getDateRange(days) {
  const end = new Date();
  end.setUTCHours(23, 59, 59, 999);

  const start = new Date();
  start.setUTCDate(start.getUTCDate() - days + 1);
  start.setUTCHours(0, 0, 0, 0);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

function extractItems(response) {
  if (Array.isArray(response?.data)) return response.data;
  return response?.data?.data ?? [];
}

function extractTotal(response, fallback = 0) {
  return (
    response?.pagination?.total ??
    response?.pagination?.totalItems ??
    response?.data?.total ??
    response?.data?.meta?.total ??
    fallback
  );
}

function extractTotalPages(response, totalItems = 0) {
  if (response?.pagination?.totalPages != null) {
    return response.pagination.totalPages;
  }
  return totalItems === 0 ? 0 : Math.ceil(totalItems / PAGE_SIZE);
}

function getPaginationPages(currentPage, totalPages) {
  if (totalPages <= 0) return [];

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function extractStatValue(stats, key) {
  const value = stats?.[key];
  if (value == null) return 0;
  if (typeof value === "object") return value.count ?? value.value ?? 0;
  return value;
}

function extractStatRate(stats, key) {
  const rateKey = `${key}_rate`;
  const directRate = stats?.[rateKey] ?? stats?.[`${key}Rate`];
  if (directRate != null) return directRate;

  const value = stats?.[key];
  if (value != null && typeof value === "object") return value.rate ?? null;
  return null;
}

function formatRate(rate, t) {
  if (rate == null || Number.isNaN(Number(rate))) {
    return { change: "—", period: t("dashboard.common.noComparison"), direction: "neutral" };
  }

  const numericRate = Number(rate);
  const formatted = `${Math.abs(numericRate).toFixed(1)}%`;
  return {
    change: formatted,
    period: t("dashboard.common.vsPreviousPeriod"),
    direction: numericRate >= 0 ? "up" : "down",
  };
}

function formatDate(value, locale) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getInitials(value = "") {
  const parts = value.replace(/_/g, " ").split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");
}

function avatarColor(value = "") {
  const colors = ["#DBEAFE", "#D1FAE5", "#FEF3C7", "#FCE7F3", "#E5E7EB"];
  const index = value.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
}

function TrendIndicator({ change, period, direction }) {
  if (direction === "neutral") {
    return <span className="dashboard-trend">{change}</span>;
  }

  const isPositive = direction === "up";
  return (
    <span className={`dashboard-trend ${isPositive ? "" : "dashboard-trend--down"}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {isPositive ? (
          <path
            d="M7 14l4-4 3 3 5-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M7 10l4 4 3-3 5 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
      {change} {period}
    </span>
  );
}

function StatCard({ label, value, change, period, direction }) {
  return (
    <div className="dashboard-stat-card">
      <p className="dashboard-stat-card__label">{label}</p>
      <p className="dashboard-stat-card__value">{value}</p>
      <TrendIndicator change={change} period={period} direction={direction} />
    </div>
  );
}

function StatusBadge({ status, t }) {
  const label = translateGenericStatus(t, status);
  const styleKey = String(status || "").toUpperCase();
  return <span className={`dashboard-badge ${statusStyles[styleKey] || ""}`}>{label}</span>;
}

function Customers() {
  const { t, i18n } = useTranslation();
  const dateRanges = useMemo(() => getDateRanges(t), [t]);
  const [activeTab, setActiveTab] = useState("all");
  const [dateRange, setDateRange] = useState("7");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");

  const activeStatus = tabs.find((tab) => tab.id === activeTab)?.status;
  const selectedRange = dateRanges.find((range) => range.value === dateRange) ?? dateRanges[0];
  const { startDate, endDate } = useMemo(
    () => getDateRange(selectedRange.days),
    [selectedRange.days]
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = await getCustomerStats({ startDate, endDate });
      setStats(response?.data ?? response ?? null);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    let cancelled = false;

    const loadCustomers = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getCustomers({
          status: activeStatus,
          search: debouncedSearch || undefined,
          startDate,
          endDate,
          page,
          pageSize: PAGE_SIZE,
        });

        if (cancelled) return;

        const items = extractItems(response);
        const total = extractTotal(response, items.length);
        setCustomers(items);
        setTotalItems(total);
        setTotalPages(extractTotalPages(response, total));
      } catch (fetchError) {
        if (cancelled) return;

        setCustomers([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(fetchError?.response?.data?.message || t("dashboard.customers.loadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCustomers();

    return () => {
      cancelled = true;
    };
  }, [activeStatus, debouncedSearch, startDate, endDate, page, t]);

  const tabCounts = useMemo(() => {
    if (!stats) return {};

    return {
      all: extractStatValue(stats, "total"),
      active: extractStatValue(stats, "active"),
      invited: extractStatValue(stats, "invited"),
      suspended: extractStatValue(stats, "suspended"),
    };
  }, [stats]);

  const statCards = useMemo(() => {
    const totalRate = formatRate(extractStatRate(stats, "total"), t);
    const activeRate = formatRate(extractStatRate(stats, "active"), t);
    const repeatRate = formatRate(extractStatRate(stats, "repeat_buyers"), t);
    const churnedRate = formatRate(extractStatRate(stats, "churned"), t);

    return [
      {
        label: t("dashboard.customers.totalCustomers"),
        value: statsLoading ? "—" : extractStatValue(stats, "total").toLocaleString("en-US"),
        ...totalRate,
      },
      {
        label: t("dashboard.customers.activeCustomers"),
        value: statsLoading ? "—" : extractStatValue(stats, "active").toLocaleString("en-US"),
        ...activeRate,
      },
      {
        label: t("dashboard.customers.repeatBuyers"),
        value: statsLoading ? "—" : extractStatValue(stats, "repeat_buyers").toLocaleString("en-US"),
        ...repeatRate,
      },
      {
        label: t("dashboard.customers.churned"),
        value: statsLoading ? "—" : extractStatValue(stats, "churned").toLocaleString("en-US"),
        ...churnedRate,
      },
    ];
  }, [stats, statsLoading, t]);

  const paginationPages = getPaginationPages(page, totalPages);
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItems);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  return (
    <div className="dashboard-page">
      <DashboardPageHeader title={t("dashboard.customers.title")}>
        <select
          className="dashboard-select"
          value={dateRange}
          onChange={(event) => {
            setDateRange(event.target.value);
            setPage(1);
          }}
          aria-label={t("dashboard.common.dateRange")}
        >
          {dateRanges.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </select>
        <label className="dashboard-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder={t("dashboard.customers.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={t("dashboard.customers.searchAria")}
          />
        </label>
      </DashboardPageHeader>

      <div className="dashboard-stats">
        {statCards.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      <div className="dashboard-card dashboard-card--table">
        <div className="dashboard-table-toolbar">
          <div className="dashboard-tabs" role="tablist" aria-label={t("dashboard.customers.paginationAria")}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`dashboard-tabs__item${activeTab === tab.id ? " dashboard-tabs__item--active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.id === "all" ? t("dashboard.common.all") : translateGenericStatus(t, tab.status)}
                {tabCounts[tab.id] != null && (
                  <span className="dashboard-tabs__count">
                    {tabCounts[tab.id].toLocaleString("en-US")}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>{t("dashboard.common.customer")}</th>
                <th>{t("dashboard.common.email")}</th>
                <th>{t("dashboard.customers.company")}</th>
                <th>{t("dashboard.partnership.businessType")}</th>
                <th>{t("dashboard.common.country")}</th>
                <th>{t("dashboard.common.status")}</th>
                <th>{t("dashboard.customers.joined")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>{t("dashboard.customers.loading")}</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7}>{error}</td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={7}>{t("dashboard.customers.noCustomers")}</td>
                </tr>
              ) : (
                customers.map((customer) => {
                  const name = getCustomerName(customer);
                  return (
                    <tr key={customer.id || customer.customer_id || customer.customerId}>
                      <td>
                        <div className="dashboard-table__customer">
                          <span
                            className="dashboard-table__avatar"
                            style={{ backgroundColor: avatarColor(name) }}
                            aria-hidden="true"
                          >
                            {getInitials(name)}
                          </span>
                          <span className="dashboard-table__name">{name}</span>
                        </div>
                      </td>
                      <td className="dashboard-table__email">{customer.email || "—"}</td>
                      <td>{getPartnershipField(customer, "company_name")}</td>
                      <td>{getPartnershipField(customer, "business_type")}</td>
                      <td>{getPartnershipField(customer, "country")}</td>
                      <td>
                        <StatusBadge status={customer.status} t={t} />
                      </td>
                      <td className="dashboard-table__date">
                        {formatDate(
                          customer.created_at ||
                            customer.createdAt ||
                            customer.first_assigned_at ||
                            customer.firstAssignedAt,
                          i18n.language
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-table-footer">
          <p className="dashboard-table-footer__info">
            {getShowingText(t, {
              entity: "customers",
              start: rangeStart,
              end: rangeEnd,
              total: totalItems,
            })}
          </p>
          <nav className="dashboard-pagination" aria-label={t("dashboard.customers.paginationAria")}>
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.previousPage")}
              disabled={page <= 1 || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {paginationPages.map((pageNumber, index) =>
              pageNumber === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="dashboard-pagination__ellipsis" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  className={`dashboard-pagination__page${
                    page === pageNumber ? " dashboard-pagination__page--active" : ""
                  }`}
                  aria-current={page === pageNumber ? "page" : undefined}
                  disabled={loading}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.nextPage")}
              disabled={page >= totalPages || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

export default Customers;
